import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Table, Empty } from "antd";
import {
  downloadBundleReport,
  getBundleFilterOptions,
  getSingleBundleReport,
} from "../../../service/ReportingService";
import { useParams } from "react-router-dom";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import searchIcon from "../../../assets/Search-Icon.svg";
import CommonTable from "../../../components/shared/table/Table";
import { STATUS_OPTIONS } from "../../../assets/constants/constants";
import { getBundleReportColumns } from "../../../assets/FormFeilds/CurriculumReportField";
import { useNavigate } from "react-router-dom";
import CustomInput from "../../../components/global/CustomInput/CustomInput";
import backButton from "../../../assets/back-btn.svg";
import CurriculumCoursesTable from "./CurriculumCoursesTable";
import {
  mapGroupOptions,
  mapUserOptions,
  SUB_TYPE_OPTIONS_CURRICULUM,
} from "../../../utility/commonConstant";
import usePagination from "../../../hooks/usePagination";
import CustomButton from "../../../components/shared/button/CustomButton";
import CustomLabelDropdown from "../../../components/shared/new Form/Fields/CustomLabelDropdown";
import {
  calculateTotalLearners,
  createSearchDebounce,
  debounceFunction,
  downloadExcelFile,
} from "../../../assets/commonFunctions/CommonFunction";
import { getBundleMemberColumns } from "../../../utility/coloumnConstants";
import PageLoader from "../../../components/global/Loader/PageLoader";
import { ArrowsAltOutlined, VerticalAlignMiddleOutlined } from "@ant-design/icons";
import { showSuccessMessage } from "../../../components/shared/pagination/messages";
import useSingleBundleReport from "../../../hooks/CurriculumsHooks/useSingleBundleReport";

const SingleBundleReport = () => {
  const navigate = useNavigate();
  const { bundleId } = useParams();
  const debounceRef = useRef(null);

  const [filterOptions, setFilterOptions] = useState(null);
  const [subTypeFilter, setSubTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [nestedExpandedUserKey, setNestedExpandedUserKey] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const { currentPage, pageSize, setTotal, paginationConfig,handlePageChange } = usePagination();

  const { data, loading, total, fetchReport } = useSingleBundleReport();

  useEffect(() => {
    setTotal(total);
  }, [total]);

  const fetchBundleData = useCallback(async () => {
    await fetchReport(bundleId, {
      page: currentPage,
      size: pageSize,
      subType: subTypeFilter,
      status: statusFilter,
      groupId: selectedGroupId,
      userId: selectedUserId,
      search: searchText,
    });
  }, [
    bundleId,
    currentPage,
    pageSize,
    subTypeFilter,
    statusFilter,
    selectedGroupId,
    selectedUserId,
    searchText,
    fetchReport,
  ]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const delay = searchText ? 500 : 0;
    debounceRef.current = setTimeout(() => {
      fetchBundleData();
    }, delay);
    return () => clearTimeout(debounceRef.current);
  }, [fetchBundleData]);

  const fetchFilterOptions = useCallback(async () => {
    const result = await getBundleFilterOptions(bundleId);
    if (result.success) setFilterOptions(result.data);
  }, [bundleId]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const result = await downloadBundleReport(bundleId);
      if (!result.success) { console.error(result.data); return; }
      downloadExcelFile(
        result.data,
        `${filterOptions?.bundleDetails?.name || "Bundle"}_Report`,
      );
      showSuccessMessage("Report Downloaded Successfully!");
    } finally {
      setExportLoading(false);
    }
  };

  const groupOptions = useMemo(() => mapGroupOptions(filterOptions?.groups), [filterOptions]);
  const userOptions = useMemo(() => mapUserOptions(filterOptions?.users), [filterOptions]);
  const displayData = useMemo(() => data?.content || [], [data]);

  const getRowKey = (record) =>
    record.type === "GROUP" ? `group-${record.groupId}` : `user-${record.userId}`;

  const renderExpandedRow = (record) => {
    if (record.type === "GROUP") {
      return (
        <div className="px-4 py-2">
          <h4 className="font-medium text-sm text-gray-700 mb-2">
            Users in {record.groupName}
          </h4>
          <Table
            dataSource={record.members || []}
            columns={getBundleMemberColumns()}
            expandable={{
              expandedRowRender: (member) => (
                <CurriculumCoursesTable
                  courses={member.courses || []}
                  rowKeyPrefix={`group-${record.groupId}-user-${member.userId}-`}
                />
              ),
              expandedRowKeys: nestedExpandedUserKey ? [nestedExpandedUserKey] : [],
              onExpand: (expanded, member) =>
                setNestedExpandedUserKey(expanded ? member.userId : null),
            }}
            pagination={false}
            rowKey="userId"
          />
        </div>
      );
    }
    if (record.type === "USER") {
      return (
        <div className="px-4 py-2">
          <h4 className="font-medium text-sm text-gray-700 mb-2">
            Courses for {record.fullName}
          </h4>
          <CurriculumCoursesTable
            courses={record.courses || []}
            rowKeyPrefix={`user-${record.userId}-`}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)] gap-4">
      <PageLoader visible={exportLoading} />
      <div className="flex flex-col">
        <div className="flex justify-between items-center">
          <h1 className="text-[#e74860] text-[1.5rem] font-normal">
            {filterOptions?.bundleDetails?.name
              ? `${filterOptions.bundleDetails.name} Report`
              : "Bundle Report"}
          </h1>
          <div className="flex gap-2 justify-center cursor-pointer" onClick={() => navigate(-1)}>
            <img alt="back-btn" src={backButton} />
            <span>Back</span>
          </div>
        </div>

        <div className="flex gap-4 mt-4">
          <div className="flex-1">
            <CustomInput
              icon={<img src={searchIcon} alt="search" className="w-5 h-5" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search users or groups"
              allowClear
            />
          </div>

          <div className="flex-1">
            <CustomLabelDropdown
              value={subTypeFilter}
              onChange={(val) => {
                setSubTypeFilter(val);
                setSelectedGroupId(null);
                setSelectedUserId(null);
                setExpandedRowKey(null);
                setNestedExpandedUserKey(null);
              }}
              options={SUB_TYPE_OPTIONS_CURRICULUM}
              placeholder="Select User or Group"
              wrapperClass="w-full"
              allowClear
            />
          </div>

          {subTypeFilter === "GROUP" && (
            <div className="flex-1">
              <CustomLabelDropdown
                value={selectedGroupId}
                onChange={(val) => { setSelectedGroupId(val); setExpandedRowKey(null); setNestedExpandedUserKey(null); }}
                options={groupOptions}
                placeholder="All Groups"
                wrapperClass="w-full"
              />
            </div>
          )}

          {subTypeFilter === "USER" && (
            <div className="flex-1">
              <CustomLabelDropdown
                value={selectedUserId}
                onChange={(val) => { setSelectedUserId(val); setExpandedRowKey(null); setNestedExpandedUserKey(null); }}
                options={userOptions}
                placeholder="All Users"
                wrapperClass="w-full"
              />
            </div>
          )}

          <div className="flex-1">
            <CustomLabelDropdown
              value={statusFilter}
              onChange={(val) => { setStatusFilter(val); setExpandedRowKey(null); setNestedExpandedUserKey(null); }}
              options={STATUS_OPTIONS}
              placeholder="Select Status"
              wrapperClass="w-full"
              allowClear
            />
          </div>

          <CustomButton className="w-30" onClick={handleExport} variant="primary">
            Export
          </CustomButton>
        </div>
      </div>

      <CommonTable
        columns={getBundleReportColumns()}
        data={expandedRowKey ? displayData.filter((row) => getRowKey(row) === expandedRowKey) : displayData}
        rowKey={getRowKey}
        scroll={{ y: "60vh" }}
        serverSide={false}
        pagination={paginationConfig}
        showLoader={loading}
        onChange={handlePageChange}
        expandable={{
          expandedRowRender: renderExpandedRow,
          expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
          onExpand: (expanded, record) => {
            setExpandedRowKey(expanded ? getRowKey(record) : null);
            setNestedExpandedUserKey(null);
          }
        }}
      />
    </div>
  );
};

export default AdminHOC(SingleBundleReport);
